import { useCallback, useContext, useState } from "react";
import { View, Image, StyleSheet } from "react-native";
import { Typography, useTheme } from "@smartrent/ui";
import { Launch } from "@smartrent/icons";

import { useUpdateOrganizationMutation } from "@/api/organizations";

import Context from "@/layout/Context";
import Card from "@/common/Card";
import { useDrawerNav } from "@/common/AppDrawer";
import { StatusBadgeDetailView } from "@/common/StatusBadgeDetailView";
import { useCompanies } from "@/api/alloy-install/companies";
import { salesforceUrl } from "@/utils";

import OrganizationInfoHeader from "./organization-info/OrganizationInfoHeader";
import { getOrganizationActions } from "./organization-info/OrganizationInfoActions";
import { useOrganization } from "./OrganizationContext";

export default function OrganizationInfo() {
  const { colors } = useTheme();
  const drawer = useDrawerNav();
  const context = useContext(Context);
  const { organization, refetchOrganization } = useOrganization();
  const {
    user: { permissions },
  } = useContext(Context);

  const { data } = useCompanies({ org_id: organization.id });

  const [isEditingNetsuiteId, setIsEditingNetsuiteId] = useState(false);
  const [netsuiteIdValue, setNetsuiteIdValue] = useState(
    organization.netsuite_id || ""
  );
  const [updateOrganization, { isLoading: isSavingNetsuiteId }] =
    useUpdateOrganizationMutation({
      onSuccess: () => {
        setIsEditingNetsuiteId(false);
        refetchOrganization();
      },
      errorToast: () => ({
        message: "Failed to update NetSuite ID.",
      }),
    });

  const organizationImage = organization?.logo_url;

  const handleUpdateOrganizationActionClick = useCallback(
    () =>
      drawer.push("updateOrganization", { organization, refetchOrganization }),
    [drawer, organization, refetchOrganization]
  );

  const handleAlloyInstallOrganizationClick = useCallback(() => {
    drawer.push("alloyInstallEnvironment", { organization });
  }, [drawer, organization]);

  const handleLinkIntegrationClick = useCallback(() => {
    drawer.push("linkIntegrationToOrg", { organization, refetchOrganization });
  }, [drawer, organization, refetchOrganization]);

  return (
    <Card
      heading={<OrganizationInfoHeader />}
      actions={getOrganizationActions({
        organization,
        context,
        permissions,
        hasAlloyInstallCompany: !!(data && data.total_records > 0),
        handleUpdateOrganizationActionClick,
        handleAlloyInstallOrganizationClick,
        handleLinkIntegrationClick,
      })}
    >
      {organizationImage ? (
        <View style={styles.card}>
          <Image
            style={styles.organizationImage}
            source={{ uri: organizationImage }}
            resizeMode="contain"
          />
        </View>
      ) : null}
      <View style={styles.orgDetails}>
        <Typography>
          SAML
          {organization.saml_provider_name && organization.saml_provider_domain
            ? ` (${organization.saml_provider_domain})`
            : null}
        </Typography>
        <StatusBadgeDetailView
          condition={organization?.saml_provider_name ? true : false}
        />
      </View>
      <View style={styles.salesforce}>
        <Typography type="bodySmall">
          Salesforce Id:
          {organization.salesforce_id ? (
            <a
              href={`${salesforceUrl()}/lightning/r/Account/${
                organization.salesforce_id
              }/view`}
              target="_blank"
              rel="noreferrer"
            >
              {" "}
              {organization.salesforce_id}{" "}
              <Launch size={8} color={colors.textTitle} />
            </a>
          ) : (
            " Not Linked"
          )}
        </Typography>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {isEditingNetsuiteId ? (
            <>
              <Typography type="bodySmall">NetSuite Id:</Typography>
              <input
                type="text"
                value={netsuiteIdValue}
                onChange={(e) => setNetsuiteIdValue(e.target.value)}
                style={{
                  fontSize: 13,
                  padding: "2px 6px",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  width: 160,
                }}
                autoFocus
              />
              <button
                onClick={() =>
                  updateOrganization({
                    id: organization.id,
                    netsuite_id: netsuiteIdValue.trim() || null,
                  })
                }
                disabled={isSavingNetsuiteId}
                className="a u-font11"
              >
                {isSavingNetsuiteId ? "Saving..." : "Save"}
              </button>
              <button
                onClick={() => {
                  setNetsuiteIdValue(organization.netsuite_id || "");
                  setIsEditingNetsuiteId(false);
                }}
                disabled={isSavingNetsuiteId}
                className="a u-font11"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <Typography type="bodySmall">
                NetSuite Id:
                {organization.netsuite_id
                  ? ` ${organization.netsuite_id}`
                  : " Not Linked"}
              </Typography>
              {permissions.update_organization &&
              permissions.id_integration_manager ? (
                <button
                  onClick={() => {
                    setNetsuiteIdValue(organization.netsuite_id || "");
                    setIsEditingNetsuiteId(true);
                  }}
                  className="a u-font11"
                >
                  Edit
                </button>
              ) : null}
            </>
          )}
        </div>
      </View>
    </Card>
  );
}

const styles = StyleSheet.create({
  card: {
    maxWidth: 480,
  },
  orgDetails: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 8,
  },
  salesforce: {
    padding: 8,
  },
  organizationImage: {
    height: 275,
  },
});
